import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowRight, Loader2, Mail, X } from "lucide-react";
import axios from "axios";
import VerifyEmailModal from "@/components/VerifyEmailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    name: "Trial",
    monthlyUSD: 0,
    monthlyMXN: 0,
    yearlyUSD: 0,
    yearlyMXN: 0,
    desc: "Explore the platform, no card needed",
    features: ["2 analyses / month", "Basic Ad Score"],
    cta: "Start Free",
    popular: false,
    trial: false,
    isTrial: true,
    icon: "✨",
  },
  {
    name: "Starter",
    monthlyUSD: 39,
    monthlyMXN: 699,
    yearlyUSD: 374,
    yearlyMXN: 6710,
    desc: "For individuals exploring ad intelligence",
    features: ["5 analyses / month", "Attention Heatmap", "Brand Attention", "Full Neuro Score", "Engagement Prediction", "Memorability Score", "AI Recommendations", "Audience Segmentation"],
    cta: "Get Started",
    popular: false,
    trial: false,
    icon: "🎯",
  },
  {
    name: "Growth",
    monthlyUSD: 89,
    monthlyMXN: 1595,
    yearlyUSD: 854,
    yearlyMXN: 15312,
    desc: "For teams optimizing ad performance",
    features: ["20 analyses / month", "Everything in Starter", "AI Image Optimization", "PDF Reports", "Shareable Link"],
    cta: "Get Started",
    popular: true,
    trial: false,
    icon: "🚀",
  },
  {
    name: "Agency",
    monthlyUSD: 219,
    monthlyMXN: 3925,
    yearlyUSD: 2102,
    yearlyMXN: 37680,
    desc: "For agencies managing multiple brands",
    features: ["60 analyses / month", "Everything in Growth", "Team Collaboration", "Priority Support"],
    cta: "Get Started",
    popular: false,
    trial: false,
    icon: "⚡",
  },
  {
    name: "Enterprise",
    monthlyUSD: null,
    monthlyMXN: null,
    yearlyUSD: null,
    yearlyMXN: null,
    desc: "Tailored for large organizations",
    features: ["Personalized analyses / month", "Everything in Agency", "White-Label Reports", "Personalized Support"],
    cta: "Contact Sales",
    popular: false,
    trial: false,
    icon: "🏢",
  },
];

export default function PricingSection() {
  const [billing, setBilling] = useState("monthly");
  const [currency, setCurrency] = useState("USD");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showSignup, setShowSignup] = useState(null);
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupError, setSignupError] = useState(null);
  const [signupTerms, setSignupTerms] = useState(false);
  const [signupMarketing, setSignupMarketing] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = async (planName) => {
    if (planName === "Enterprise") {
      window.location.href = "/contact?message=" + encodeURIComponent("I would like to receive more information about the Enterprise plan. I'm looking for:");
      return;
    }
    if (planName === "Trial") {
      window.location.href = "/login?mode=register";
      return;
    }
    setCheckoutLoading(planName);
    try {
      const { data } = await axios.post(`${API}/stripe/checkout`, {
        plan: planName.toLowerCase(),
        billing: billing === "yearly" ? "yearly" : "monthly",
        currency: currency.toLowerCase()
      }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      if (err.response?.status === 401) {
        setCheckoutLoading(null);
        setShowSignup(planName);
        setSignupError(null);
      } else if (err.response?.status === 403 && (err.response?.data?.detail || "").toLowerCase().includes("verify")) {
        setCheckoutLoading(null);
        setVerifyModal(true);
      } else {
        alert(err.response?.data?.detail || "Something went wrong. Please try again.");
        setCheckoutLoading(null);
      }
    }
  };

  const handleGuestCheckout = async (e) => {
    e.preventDefault();
    if (!signupName.trim() || !signupEmail.trim() || !signupPassword.trim()) return;
    if (signupPassword.length < 8) { setSignupError("Password must be at least 8 characters"); return; }
    if (!signupTerms) { setSignupError("You must accept the Terms and Conditions and Privacy Policy"); return; }
    setCheckoutLoading(showSignup);
    setSignupError(null);
    try {
      const { data } = await axios.post(`${API}/stripe/guest-checkout`, {
        name: signupName.trim(), email: signupEmail.trim(), password: signupPassword,
        plan: showSignup.toLowerCase(),
        billing: billing === "yearly" ? "yearly" : "monthly",
        currency: currency.toLowerCase()
      }, { withCredentials: true });
      if (data.access_token) localStorage.setItem("vizmo_token", data.access_token);
      window.location.href = data.url;
    } catch (err) {
      setSignupError(err.response?.data?.detail || "Something went wrong.");
      setCheckoutLoading(null);
    }
  };

  const getPlanPrice = (plan) => {
    if (plan.monthlyUSD === null) return null;
    if (plan.isTrial) return 0;
    if (currency === "MXN") return billing === "yearly" ? plan.yearlyMXN : plan.monthlyMXN;
    return billing === "yearly" ? plan.yearlyUSD : plan.monthlyUSD;
  };

  const formatPrice = (amount) => {
    if (amount === null) return null;
    if (amount === 0) return "$0";
    return `$${amount.toLocaleString()}`;
  };

  const currencyLabel = currency === "MXN" ? "MXN" : "USD";
  const periodLabel = billing === "yearly" ? "/yr" : "/mo";

  return (
    <section id="pricing" className="py-24 relative overflow-hidden" data-testid="pricing-section">
      {/* Subtle background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03] pointer-events-none"
        style={{ background: "radial-gradient(circle, #F97316 0%, transparent 70%)" }} />

      <div className="max-w-[1280px] mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
            Simple, <span className="gradient-text">Transparent</span> Pricing
          </h2>
          <p className="text-slate-500 mt-3 text-sm max-w-md mx-auto">Start free. Scale as you grow. No hidden fees.</p>

          {/* Launch promo banner — only for monthly plans */}
          {billing === "monthly" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full text-white text-sm font-bold shadow-lg"
              style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}
            >
              <span className="text-lg">🎉</span>
              <span>Launch Offer: 50% OFF your first month — automatically applied at checkout</span>
            </motion.div>
          )}
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex items-center justify-center gap-3 mb-14"
        >
          {/* Billing toggle */}
          <div className="flex items-center bg-slate-100/80 backdrop-blur rounded-full p-1 border border-slate-200/50">
            <button onClick={() => setBilling("monthly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${billing === "monthly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Monthly
            </button>
            <button onClick={() => setBilling("yearly")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1.5 ${billing === "yearly" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              Yearly
              <span className="text-[10px] font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 px-2 py-0.5 rounded-full">SAVE 20%</span>
            </button>
          </div>

          {/* Currency toggle */}
          <div className="flex items-center bg-slate-100/80 backdrop-blur rounded-full p-1 border border-slate-200/50">
            <button onClick={() => setCurrency("USD")}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${currency === "USD" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              $ USD
            </button>
            <button onClick={() => setCurrency("MXN")}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-all duration-300 ${currency === "MXN" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              $ MXN
            </button>
          </div>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 max-w-[1300px] mx-auto items-stretch">
          {plans.map((plan, i) => {
            const price = getPlanPrice(plan);
            const isEnterprise = plan.monthlyUSD === null;
            const isPopular = plan.popular;

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
                className={`relative flex ${isPopular ? "lg:-mt-3 lg:mb-[-12px]" : ""}`}
              >
                {isPopular ? (
                  <div className="flex flex-col w-full gradient-border pricing-glow rounded-2xl">
                    <div className="gradient-border-inner p-0 rounded-2xl flex flex-col flex-1">
                      {/* Popular badge */}
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-gradient-to-r from-orange-500 to-fuchsia-500 text-white text-[11px] font-bold px-4 py-1.5 rounded-full flex items-center gap-1 shadow-lg shadow-orange-500/25">
                          <Sparkles size={12} /> Most Popular
                        </span>
                      </div>
                      <div className="p-6 pt-8 flex flex-col flex-1">
                        <CardContent plan={plan} price={price} formatPrice={formatPrice} currencyLabel={currencyLabel} periodLabel={periodLabel}
                          billing={billing} currency={currency} isPopular onCheckout={handleCheckout} checkoutLoading={checkoutLoading} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`w-full flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:shadow-xl hover:shadow-slate-200/50 ${
                    isEnterprise ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}>
                    <CardContent plan={plan} price={price} formatPrice={formatPrice} currencyLabel={currencyLabel} periodLabel={periodLabel}
                      billing={billing} currency={currency} isDark={isEnterprise} onCheckout={handleCheckout} checkoutLoading={checkoutLoading} />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Signup modal for guest checkout */}
        {showSignup && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowSignup(null); setSignupError(null); }}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-slate-800 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Start your {showSignup} plan
              </h3>
              <p className="text-sm text-slate-500 mb-5">Create your account to continue to checkout</p>
              <form onSubmit={handleGuestCheckout} className="space-y-3">
                <input type="text" placeholder="Full name" value={signupName} onChange={e => setSignupName(e.target.value)} required
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
                <input type="email" placeholder="Email address" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} required
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
                <input type="password" placeholder="Create password (min 8 chars)" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} required minLength={8}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
                <label className="flex items-start gap-2 cursor-pointer pt-1">
                  <input type="checkbox" checked={signupTerms} onChange={e => setSignupTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                  <span className="text-xs text-slate-500 leading-relaxed">
                    I accept the <a href="/terms" target="_blank" className="text-orange-600 hover:underline font-medium">Terms and Conditions</a> and <a href="/privacy" target="_blank" className="text-orange-600 hover:underline font-medium">Privacy Policy</a>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={signupMarketing} onChange={e => setSignupMarketing(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                  <span className="text-xs text-slate-500 leading-relaxed">I agree to receive marketing communications</span>
                </label>
                {signupError && <p className="text-xs text-red-500">{signupError}</p>}
                <Button type="submit" disabled={checkoutLoading === showSignup}
                  className="gradient-btn rounded-xl w-full h-12 font-semibold text-sm mt-2">
                  {checkoutLoading === showSignup ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <span className="flex items-center gap-2">Continue to payment <ArrowRight size={14} /></span>
                  )}
                </Button>
              </form>
              <p className="text-xs text-slate-400 text-center mt-4">
                Already have an account?{" "}
                <a href="/login" className="text-orange-600 font-medium hover:underline">Log in</a>
              </p>
            </div>
          </div>
        )}

        {verifyModal && <VerifyEmailModal onClose={() => { setVerifyModal(false); setResentVerify(false); }} />}
      </div>
    </section>
  );
}

function CardContent({ plan, price, formatPrice, currencyLabel, periodLabel, billing, currency, isPopular, isDark, onCheckout, checkoutLoading }) {
  const isEnterprise = plan.monthlyUSD === null;
  const isTrial = plan.isTrial;
  const textPrimary = isDark ? "text-white" : "text-slate-800";
  const textSecondary = isDark ? "text-slate-400" : "text-slate-500";
  const textFeature = isDark ? "text-slate-300" : "text-slate-600";
  const checkColor = isDark ? "text-orange-400" : "text-orange-500";

  return (
    <>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base">{plan.icon}</span>
          <h3 className={`text-lg font-bold ${textPrimary}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {plan.name}
          </h3>
        </div>
        <p className={`text-xs ${textSecondary} leading-relaxed`}>{plan.desc}</p>
      </div>

      {/* Price */}
      <div className="mb-5">
        {isEnterprise ? (
          <div>
            <span className={`text-3xl font-bold ${textPrimary}`} style={{ fontFamily: 'Outfit, sans-serif' }}>Custom</span>
            <p className={`text-xs ${textSecondary} mt-1`}>Tailored to your needs</p>
          </div>
        ) : isTrial ? (
          <div>
            <span className={`text-4xl font-bold ${textPrimary}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>$0</span>
            <p className={`text-xs ${textSecondary} mt-1`}>No credit card required</p>
          </div>
        ) : (
          <div>
            {billing === "monthly" ? (
              <>
                {/* Discount badge */}
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md mb-2 text-white text-[10px] font-bold" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                  🎉 50% OFF FIRST MONTH
                </div>
                {/* Original price crossed out */}
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-base line-through ${textSecondary}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatPrice(price)}
                  </span>
                  <span className={`text-[10px] ${textSecondary}`}>{currencyLabel}</span>
                </div>
                {/* Discounted price — large and prominent */}
                <div className="flex items-baseline gap-1">
                  <span className={`text-5xl font-bold ${textPrimary}`} style={{ fontFamily: 'JetBrains Mono, monospace', background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {formatPrice(Math.round(price / 2))}
                  </span>
                  <span className={`text-xs font-medium ${textSecondary}`}>{currencyLabel}</span>
                </div>
                <span className={`text-xs ${textSecondary}`}>first month</span>
                <p className={`text-[11px] mt-1 ${textSecondary}`}>then {formatPrice(price)} {currencyLabel}/mo</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1">
                  <span className={`text-4xl font-bold ${textPrimary}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {formatPrice(price)}
                  </span>
                  <span className={`text-xs font-medium ${textSecondary}`}>{currencyLabel}</span>
                </div>
                <span className={`text-xs ${textSecondary}`}>{periodLabel}</span>
                <p className="text-[11px] text-emerald-500 font-semibold mt-1">Save with annual billing</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? "bg-orange-500/15" : "bg-orange-50"}`}>
              <Check size={10} className={checkColor} strokeWidth={3} />
            </div>
            <span className={`text-sm ${textFeature}`}>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {isPopular ? (
        <Button data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
          onClick={() => onCheckout(plan.name)}
          disabled={checkoutLoading === plan.name}
          className="gradient-btn rounded-xl w-full h-12 font-semibold text-sm">
          {checkoutLoading === plan.name ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <span className="flex items-center gap-2">{plan.cta} <ArrowRight size={14} /></span>
          )}
        </Button>
      ) : isEnterprise ? (
        <Button data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
          onClick={() => onCheckout(plan.name)}
          className="rounded-xl w-full h-12 font-semibold text-sm bg-white text-slate-900 hover:bg-slate-100 border-0">
          <span className="flex items-center gap-2">{plan.cta} <ArrowRight size={14} /></span>
        </Button>
      ) : (
        <Button data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
          onClick={() => onCheckout(plan.name)}
          disabled={checkoutLoading === plan.name}
          className="rounded-xl w-full h-12 font-semibold text-sm border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all">
          {checkoutLoading === plan.name ? (
            <Loader2 size={16} className="animate-spin text-slate-400" />
          ) : (
            <span>{plan.cta}</span>
          )}
        </Button>
      )}
    </>
  );
}
